import dayjs from '@/utils/dayjs'
import { getDayDescriptionToday, getWeatherSymbol } from '@/utils/kmm'
import {
  Daybox,
  DayboxAttributes,
  DayboxHourlyEntry,
} from '@/services/api/bff/weather/dtos/Dayboxes.dto'

export type ParsedHourly = DayboxHourlyEntry & {
  localDateTime: string
}

export type ParsedDayboxAttributes = Daybox['attributes'] & {
  hourly: ParsedHourly[]
}

export type ParsedDaybox = Daybox & ParsedDayboxAttributes

// derive offset in minutes from a datetime‐string
function calculateTimezoneOffsetInMinutes(dateTime: string): number {
  return dayjs.utc(dateTime).utcOffset()
}

/**
 * Format a datetime string with a given offset, returning parsed fields.
 */
function formatDateTime(utcIso: string, tz: string) {
  const d = dayjs.utc(utcIso).tz(tz)
  return {
    hour: d.hour(),
    day: d.date(),
    month: d.month() + 1,
    time: d.format('HH:mm'),
    date: d.toDate(),
    dateToHour: d.startOf('hour').toISOString(),
  }
}

function getCurrentLocalTime(tz: string) {
  const d = dayjs().tz(tz)
  return {
    hour: d.hour(),
    day: d.date(),
    month: d.month() + 1,
    time: d.format('HH:mm'),
    date: d.toDate(),
    dateToHour: d.startOf('hour').toISOString(),
  }
}

/**
 * Assign a label "Heute", "Morgen", "Gestern" or weekday name based on date.
 */
function assignLabel(dateString: string, tz: string): string {
  const now = dayjs().tz(tz)
  const target = dayjs.utc(dateString).tz(tz)

  if (target.isSame(now, 'day')) return 'Heute'
  if (target.isSame(now.add(1, 'day'), 'day')) return 'Morgen'
  return target.format('dddd')
}

/**
 * Determine second-day logic: is the second daybox still "today"?
 */
function secondDayboxToday(date1: string, date2: string): boolean {
  return !dayjs(date1).isAfter(dayjs(date2))
}

type RestOfDayHourly = Omit<DayboxHourlyEntry, 'temp'> & {
  temp: string
  isNow: boolean
  localTime: string
  localHour: number
  localDay: number
  localMonth: number
  localDateTime: string
}

/**
 * Returns the rest of the day's hourly entries from now onwards, floored to current hour.
 */
function getRestOfDayHourly(hourlyArr: RestOfDayHourly[]): RestOfDayHourly[] {
  const nowHour = dayjs().utc().startOf('hour')
  return hourlyArr.filter(
    (entry) => !dayjs(entry.dateTime).utc().isBefore(nowHour),
  )
}

/**
 * Remove any Daybox whose entire UTC‐timestamp block lies before `now` and return just now and the next 3 days.
 *
 * @param dayboxes  The raw array from your API
 * @param tz        IANA timezone name, e.g. "Europe/Berlin"
 * @param nowIso    Current local‐time ISO in that tz, e.g. obtained via dayjs().tz(tz).toISOString()
 */
function filterDayboxes(
  dayboxes: Daybox[],
  tz: string,
  nowIso: string,
): Daybox[] {
  const now = dayjs(nowIso).tz(tz)

  return dayboxes.filter((db) => {
    const hrs = db.attributes.hourly
    if (!hrs.length) return false

    const lastUtc = hrs[hrs.length - 1].dateTime
    const lastLocal = dayjs.tz(lastUtc, tz)
    return lastLocal.isSameOrAfter(now) && lastLocal.isBefore(now.add(4, 'day'))
  })
}

/**
 * Transforms the daybox response data to a usable format
 *
 * TODO remove this to use just new api endpoint
 *
 * @param attributes  The daybox response date
 * @param timeZone    The timezone of the location
 * @param nowInfo     The information about the current date
 */
function transformHourlyData(
  attributes: DayboxAttributes,
  timeZone: any,
  nowInfo: any,
) {
  const hourly = attributes.hourly.filter((h) => h.dateTime)

  return hourly.map((h: DayboxHourlyEntry) => {
    const ft = formatDateTime(h.dateTime, timeZone)
    return {
      ...h,
      temp: h.temp.toFixed(),
      isNow: ft.hour === nowInfo.hour && ft.day === nowInfo.day,
      localTime: ft.time,
      localHour: ft.hour,
      localDay: ft.day,
      localMonth: ft.month,
      localDateTime: ft.dateToHour,
    }
  })
}

/**
 * Transforms the daybox response data to a usable format
 *
 * TODO remove this to use just new api endpoint
 *
 * @param daybox  The daybox response data
 * @param idx     The index of the daybox
 * @param tz      The timezone of the location
 * @param nowInfo The information about the current date
 */
function transformDaybox(daybox: Daybox, idx: number, tz: any, nowInfo: any) {
  const processedHourly = transformHourlyData(daybox.attributes, tz, nowInfo)
  const restOfDay = idx === 0 ? getRestOfDayHourly(processedHourly) : []
  const sunrise = formatDateTime(
    daybox.attributes.sunrise,
    tz,
  ).date.toISOString()
  const sunset = formatDateTime(daybox.attributes.sunset, tz).date.toISOString()
  const description =
    idx === 0
      ? getDayDescriptionToday(
          processedHourly.filter(
            (h) => h.localDay === nowInfo.day && h.localHour >= nowInfo.hour,
          ),
          sunrise,
          sunset,
          daybox,
          null,
          true,
          daybox.attributes.windDirection,
          new Date(nowInfo.date.getTime()),
        )
      : '...'

  return {
    ...daybox,
    description,
    isToday: idx === 0,
    weekDay: assignLabel(processedHourly[0].dateTime, tz),
    attributes: {
      ...daybox.attributes,
      symbol:
        idx === 0
          ? getWeatherSymbol(restOfDay.map((r) => ({ ...r, symbol: r.symbol })))
          : daybox.attributes.symbol,
      sunrise,
      sunset,
      hourly: processedHourly,
    },
  }
}

export {
  type RestOfDayHourly,
  calculateTimezoneOffsetInMinutes,
  formatDateTime,
  getCurrentLocalTime,
  assignLabel,
  secondDayboxToday,
  getRestOfDayHourly,
  filterDayboxes,
  transformDaybox,
  transformHourlyData,
}
